import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { ANNIE_STUDIO, FONT_SIZE, INK, NEUTRAL, RADIUS } from "@/theme/tokens";
import { AssistantAvatarKind } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import { SystemFlatSwitch } from "@/ui/Clickable/SystemFlatSwitch";
import { SystemSwitch } from "@/ui/Clickable/SystemSwitch";
import { VisualChoiceGrid, VisualChoiceShape } from "@/ui/Clickable/VisualChoiceGrid";
import { AnnieStudioCharacterParts } from "./AnnieStudioCharacterParts";
import { AnnieStudioHumanParts } from "./AnnieStudioHumanParts";
import { AnnieStudioNameField } from "./AnnieStudioNameField";
import { AnnieStudioSection } from "./AnnieStudioSection";
import { ANNIE_AVATAR_PRESET_CONFIGS, ANNIE_AVATAR_PRESETS, type AnnieAvatarPreset, HALLOWEEN_AVATAR_PRESETS } from "./annieAvatarPresets";
import { kebab, miniAvatar } from "./annieStudioOptions";
import type { AnnieNameDraft } from "./useAnnieNameDraft";
import type { AnnieStudioDraft } from "./useAnnieStudioDraft";

type Props = {
	studio: AnnieStudioDraft;
	name: AnnieNameDraft;
	defaultName: string;
	isHidden: boolean;
	onShowChange: (show: boolean) => void;
	isSettingHidden: boolean;
	/** A save, rename or reset is in flight. */
	disabled: boolean;
};

const KIND_OPTIONS: Record<AssistantAvatarKind, { labelKey: string; testId: string }> = {
	[AssistantAvatarKind.Character]: { labelKey: "annie.avatar.kind.character", testId: "annie-studio-kind-character" },
	[AssistantAvatarKind.Human]: { labelKey: "annie.avatar.kind.human", testId: "annie-studio-kind-human" },
};

const presetChoices = (t: TFunction, presets: AnnieAvatarPreset[]) =>
	presets.map((preset) => ({
		value: preset.id,
		label: t(`annie.studio.preset.${preset.id}`),
		dataTestId: `annie-studio-preset-${kebab(preset.id)}`,
		content: miniAvatar(preset.config, ANNIE_STUDIO.PRESET_AVATAR, `annie-studio-preset-${kebab(preset.id)}-preview`),
	}));

/** The Studio's right-hand column: the off switch, the name, presets, the style switch, then that style's parts. */
export const AnnieStudioControls: FC<Props> = ({ studio, name, defaultName, isHidden, onShowChange, isSettingHidden, disabled }) => {
	const { t } = useTranslation();
	const { draft } = studio;
	const isHalloweenSeason = useIsHalloweenSeason();
	const presetOptions = useMemo(() => presetChoices(t, ANNIE_AVATAR_PRESETS), [t]);
	const halloweenOptions = useMemo(() => presetChoices(t, HALLOWEEN_AVATAR_PRESETS), [t]);
	const kindOptions = Object.values(AssistantAvatarKind).map((kind) => ({
		value: kind,
		label: t(KIND_OPTIONS[kind].labelKey),
		"data-testid": KIND_OPTIONS[kind].testId,
	}));
	// Costumes are offered all year; in Halloween week they lead, the rest of the year they follow the everyday looks.
	const halloweenSection = (
		<AnnieStudioSection title={t("annie.studio.presets.halloween")}>
			{(labelId) => (
				<VisualChoiceGrid
					ariaLabelledBy={labelId}
					size="SM"
					shape={VisualChoiceShape.Circle}
					disabled={disabled}
					value={studio.matchingPresetId}
					onChange={(id) => studio.applyPreset({ id, config: ANNIE_AVATAR_PRESET_CONFIGS[id] })}
					options={halloweenOptions}
					dataTestId="annie-studio-presets-halloween"
				/>
			)}
		</AnnieStudioSection>
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
			<Box
				sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.25, border: `1px solid ${NEUTRAL[200]}`, borderRadius: `${RADIUS.CONTROL}px` }}
			>
				<SystemSwitch
					checked={!isHidden}
					onChange={(event) => onShowChange(event.target.checked)}
					ariaLabel={t("annie.studio.show.label")}
					loading={isSettingHidden}
					disabled={disabled}
					dataTestId="annie-studio-show-switch"
				/>
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontWeight: 700, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{t("annie.studio.show.label")}</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("annie.studio.show.off_hint")}</Typography>
				</Box>
			</Box>

			<AnnieStudioNameField name={name} defaultName={defaultName} disabled={disabled} />

			{isHalloweenSeason && halloweenSection}

			<AnnieStudioSection title={t("annie.studio.presets")}>
				{(labelId) => (
					<VisualChoiceGrid
						ariaLabelledBy={labelId}
						size="SM"
						shape={VisualChoiceShape.Circle}
						disabled={disabled}
						value={studio.matchingPresetId}
						onChange={(id) => studio.applyPreset({ id, config: ANNIE_AVATAR_PRESET_CONFIGS[id] })}
						options={presetOptions}
						dataTestId="annie-studio-presets"
					/>
				)}
			</AnnieStudioSection>

			{!isHalloweenSeason && halloweenSection}

			<AnnieStudioSection
				title={t("annie.studio.style")}
				action={
					<SystemFlatSwitch
						options={kindOptions}
						activeIndexOrValue={draft.kind}
						onSwitch={(option) => studio.setKind(option.value)}
						delayCallback={0}
						equalWidthOptions
						textWrap="nowrap"
						data-testid="annie-studio-kind"
					/>
				}
			/>

			{draft.kind === AssistantAvatarKind.Character ? (
				<AnnieStudioCharacterParts draft={draft} onChange={studio.setCharacterPart} disabled={disabled} />
			) : (
				<AnnieStudioHumanParts draft={draft} onChange={studio.setHumanPart} disabled={disabled} />
			)}
		</Box>
	);
};
